import { z } from 'zod';

import { Currency, OrganizationUserType } from '../enums';
import { PickedCivilDayDto } from './civilDate';

export const CreateProductDto = z.object({
  name: z.string().min(1, 'VALIDATION__Required').max(255),
  costPrice: z.number().min(0).optional().default(0),
  salePrice: z.number().min(0),
  currency: z.nativeEnum(Currency),
  // blank = no rate entered (not the same as a 0 % rate)
  vatPercent: z.number().min(0).max(100).nullish(),
  // Required unless `isService` — a service item (sauna, a class drop-in)
  // has no quantity to track, ever.
  stockQuantity: z.number().int().min(0).optional(),
  isService: z.boolean().optional().default(false),
});

export type ApiCreateProductDto = z.infer<typeof CreateProductDto>;

export const UpdateProductDto = z
  .object({
    name: z.string().min(1, 'VALIDATION__Required').max(255).optional(),
    costPrice: z.number().min(0).optional(),
    salePrice: z.number().min(0).optional(),
    currency: z.nativeEnum(Currency).optional(),
    vatPercent: z.number().min(0).max(100).nullish(),
    stockQuantity: z.number().int().min(0).nullable().optional(),
    isService: z.boolean().optional(),
  })
  .strict();

export type ApiUpdateProductDto = z.infer<typeof UpdateProductDto>;

export const ProductOrderByEnum = z.enum([
  'name',
  'stockQuantity',
  'salePrice',
  'bestSelling',
]);
export type ApiProductOrderBy = z.infer<typeof ProductOrderByEnum>;

export const ListProductsQueryDto = z.object({
  search: z.string().trim().max(255).optional(),
  offset: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  orderBy: ProductOrderByEnum.optional().default('name'),
  orderDirection: z.enum(['ASC', 'DESC']).optional().default('ASC'),
  includeArchived: z
    .enum(['true', 'false'])
    .transform((value) => value === 'true')
    .optional(),
});

export type ApiListProductsQueryDto = z.infer<typeof ListProductsQueryDto>;

// Front-desk "who's due" naming convention aside, this is the Desk domain's
// own recipient kind — one row per recipient (plan Phase 5, decision #6),
// so a client-GROUP sale is expanded into `recipientUserIds` here and the
// backend fans it out into that many `Sale` rows.
export const CreateSaleDto = z
  .object({
    productId: z.string().uuid(),
    recipientUserIds: z
      .array(z.string().uuid())
      .min(1)
      .refine(
        (ids) => new Set(ids.map((id) => id.toLowerCase())).size === ids.length,
        { message: 'VALIDATION__NotAllowed' },
      ),
    quantity: z.number().int().min(1).optional().default(1),
    // Mutually exclusive — see the DB CHECK constraint on `Sales` and
    // `computeSaleFinalPrice`. Neither set means "full price, no discount".
    discountPercent: z.number().min(0).max(100).optional().default(0),
    discountAmount: z.number().min(0).optional().default(0),
    paymentMethod: z.enum(['CASH', 'CARD', 'TRANSFER', 'OTHER']),
    // The 0 RSD / gift-to-group case (spec) explains itself here — not a
    // Shift entity (plan Phase 5, decision #5).
    comment: z.string().max(2000).optional(),
  })
  .strict()
  .refine((dto) => dto.discountPercent === 0 || dto.discountAmount === 0, {
    message: 'VALIDATION__DiscountMutuallyExclusive',
    path: ['discountAmount'],
  });

export type ApiCreateSaleDto = z.infer<typeof CreateSaleDto>;

export const SaleRecipientTypeEnum = z.enum([
  OrganizationUserType.OWNER,
  OrganizationUserType.TRAINER,
  OrganizationUserType.CLIENT,
  OrganizationUserType.STAFF,
]);

export const ListSalesQueryDto = z.object({
  from: z.string().optional(),
  to: z.string().optional(),
  orderDirection: z.enum(['ASC', 'DESC']).optional().default('DESC'),
  offset: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});

export type ApiListSalesQueryDto = z.infer<typeof ListSalesQueryDto>;

// Storno of a desk sale. The reason is mandatory: it is the only explanation
// the audit trail keeps of why a recorded payment was struck out.
export const VoidSaleDto = z
  .object({
    reason: z.string().trim().min(1, 'VALIDATION__Required').max(2000),
  })
  .strict();

export type ApiVoidSaleDto = z.infer<typeof VoidSaleDto>;

// 2.11 — a stock-in of the desk. Amounts are in major units of `currency`; the
// gross unit cost is required, the net one is not. The service converts to
// minor units under its exactness gate.
export const CreateProductPurchaseDto = z
  .object({
    productId: z.string().uuid(),
    quantity: z.number().int().min(1).max(1_000_000),
    unitCostGross: z.number().positive(),
    unitCostNet: z.number().positive().nullish(),
    // a bare civil day that exists on the calendar ('2026-02-31' does not)
    purchasedOn: PickedCivilDayDto,
    supplierNote: z.string().trim().max(2000).optional(),
  })
  .refine(
    (dto) => dto.unitCostNet == null || dto.unitCostNet <= dto.unitCostGross,
    { message: 'VALIDATION__NetExceedsGross', path: ['unitCostNet'] },
  );
export type ApiCreateProductPurchaseDto = z.infer<
  typeof CreateProductPurchaseDto
>;

export const VoidProductPurchaseDto = z
  .object({
    reason: z.string().trim().min(1, 'VALIDATION__Required').max(2000),
  })
  .strict();
export type ApiVoidProductPurchaseDto = z.infer<typeof VoidProductPurchaseDto>;
